// ============================================================================
// Manual overrides & outbox inspector (/oms/overrides).
//
// Two jobs, and the first one matters more day to day:
//
//   INSPECT — find why an order is stuck and read the actual call. Search by order
//     number to list every unsettled document and carrier op on it (the id is the
//     thing you don't have when an order goes red), then read the exact REQUEST we
//     sent the provider next to its full reply. A rejection like Loginext's
//     "Item type" names a field; debugging it means reading that field's value, and
//     until now the only way was a shell on the server.
//
//   RESOLVE — settle a row whose write can never succeed: a truck cancelled by hand
//     at the carrier answers our CANCEL with a 409 forever. Retry (on the order page)
//     is for the opposite case, where the call could still work.
//
// Deliberately NOT in the nav. Resolving asserts something about the outside world
// the system cannot verify, so it is reached by URL by people who know it exists.
// The reason is mandatory and lands in the audit log, so a forced pass never reads
// as a clean success.
// ============================================================================
import { WarningOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Empty,
  Input,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import React, { useState } from "react";
import { fmtText } from "./format";
import { forceResolve, lookupOrderRows, lookupOutboxRow, OutboxRow } from "./oms.api";

type Kind = "document" | "logistics";

const KIND_HINT: Record<Kind, string> = {
  document: "A Zoho outbox Document id.",
  logistics: "A LogisticsOutbox op id — the carrier call (book / cancel / update), not the booking.",
};

const SETTLED = ["created", "reconciled", "done"];

const rowKind = (r: OutboxRow): Kind => (r.kind === "logistics" ? "logistics" : "document");
const rowLabel = (r: OutboxRow) =>
  fmtText(r.doc_type_display || r.doc_type || r.op || "—");

// Pretty-printed JSON in a scrollable block — the raw call, exactly as sent/received.
const Payload: React.FC<{ title: string; value: unknown }> = ({ title, value }) => (
  <div style={{ marginBottom: 8 }}>
    <Typography.Text strong style={{ fontSize: 12 }}>
      {title}
    </Typography.Text>
    <pre
      style={{
        margin: "4px 0 0",
        padding: 8,
        background: "#fafafa",
        border: "1px solid #f0f0f0",
        borderRadius: 4,
        fontSize: 11,
        maxHeight: 320,
        overflow: "auto",
      }}
    >
      {value === null || value === undefined ? "—" : JSON.stringify(value, null, 2)}
    </pre>
  </div>
);

const RowDetail: React.FC<{ row: OutboxRow; onResolved: (r: OutboxRow) => void }> = ({
  row,
  onResolved,
}) => {
  const [reason, setReason] = useState("");
  const [zohoId, setZohoId] = useState("");
  const [busy, setBusy] = useState(false);
  const kind = rowKind(row);
  const settled = SETTLED.includes(String(row.state));

  const resolve = async () => {
    setBusy(true);
    const updated = await forceResolve(kind, row.id, reason.trim(), zohoId.trim() || undefined);
    setBusy(false);
    if (updated) {
      setReason("");
      setZohoId("");
      onResolved(updated);
    }
  };

  return (
    <div>
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Row">
          {kind === "document" ? "Document" : "Carrier op"} #{row.id} · {rowLabel(row)}
        </Descriptions.Item>
        <Descriptions.Item label="State">
          <Tag color={settled ? "success" : "error"}>{fmtText(row.state)}</Tag>
          {row.attempt_count ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.attempt_count} attempt(s)
            </Typography.Text>
          ) : null}
        </Descriptions.Item>
        <Descriptions.Item label="Order">
          {row.sales_order_id ? `SO #${row.sales_order_id}` : "—"}
          {row.operation_stage_id ? ` · stage ${row.operation_stage_id}` : ""}
          {row.operation_type ? ` · ${fmtText(row.operation_type)}` : ""}
        </Descriptions.Item>
        {kind === "document" ? (
          <Descriptions.Item label="Zoho id">{fmtText(row.zoho_doc_id) || "—"}</Descriptions.Item>
        ) : (
          <Descriptions.Item label="Booking">
            {row.booking_id ? `#${row.booking_id} · ${fmtText(row.booking_lifecycle)}` : "—"}
            {row.provider_ref ? ` · ${fmtText(row.provider_ref)}` : ""}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Error">
          <span style={{ color: "#cf1322" }}>{fmtText(row.error) || "—"}</span>
        </Descriptions.Item>
      </Descriptions>

      <Collapse
        size="small"
        style={{ marginBottom: 12 }}
        items={[
          {
            key: "payloads",
            label: "Raw request / response",
            children: (
              <>
                <Payload title="Request we sent" value={row.request} />
                <Payload title="Response received" value={row.response} />
              </>
            ),
          },
        ]}
      />

      {row.manually_resolved ? (
        <Alert
          type="success"
          showIcon
          message="Already resolved by hand"
          description={
            <span style={{ fontSize: 12 }}>
              {fmtText(row.manually_resolved.reason)} — {fmtText(row.manually_resolved.actor)}, was{" "}
              {fmtText(row.manually_resolved.from_state)}
            </span>
          }
        />
      ) : settled ? (
        <Alert type="info" showIcon message="This row succeeded — nothing to override." />
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          <Input.TextArea
            rows={2}
            placeholder="Why is this safe to skip? e.g. Test-OMS-LN-162 was cancelled at the carrier before we called"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {kind === "document" && (
            <Input
              placeholder="Zoho id, if the document DOES exist there (optional)"
              value={zohoId}
              onChange={(e) => setZohoId(e.target.value)}
            />
          )}
          <Popconfirm
            title="Mark this row as settled?"
            description="The provider is not called. Anything waiting on this row will move ahead."
            okText="Resolve"
            okButtonProps={{ danger: true }}
            onConfirm={resolve}
            disabled={!reason.trim()}
          >
            <Button danger icon={<WarningOutlined />} loading={busy} disabled={!reason.trim()}>
              Mark as resolved
            </Button>
          </Popconfirm>
        </Space>
      )}
    </div>
  );
};

const OmsOverrides: React.FC = () => {
  const [mode, setMode] = useState<"order" | "row">("order");
  const [kind, setKind] = useState<Kind>("logistics");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<OutboxRow[] | null>(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    setBusy(true);
    if (mode === "order") {
      setRows(await lookupOrderRows(Number(query)));
    } else {
      const found = await lookupOutboxRow(kind, Number(query));
      setRows(found ? [found] : []);
    }
    setBusy(false);
  };

  const replace = (updated: OutboxRow) =>
    setRows((prev) =>
      (prev ?? []).map((r) => (r.kind === updated.kind && r.id === updated.id ? updated : r))
    );

  return (
    <div style={{ padding: 24, maxWidth: 980 }}>
      <Typography.Title level={4} style={{ marginBottom: 4 }}>
        Outbox inspector &amp; manual overrides
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
        Read the actual call behind a stuck order — the exact request we sent the provider and its
        reply — and, where the work has already happened outside the system, mark the row as settled
        so the order moves ahead. Marking resolved does not call the provider; use Retry on the order
        page when the call could still succeed.
      </Typography.Paragraph>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          <Radio.Group
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              setRows(null);
            }}
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: "By order", value: "order" },
              { label: "By row id", value: "row" },
            ]}
          />
          {mode === "row" && (
            <>
              <Radio.Group
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                options={[
                  { label: "Carrier op", value: "logistics" },
                  { label: "Zoho document", value: "document" },
                ]}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {KIND_HINT[kind]}
              </Typography.Text>
            </>
          )}
          {mode === "order" && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              The OMS sales-order id — the number in the order URL (/oms/<b>125</b>). Lists every
              document and carrier op on it that has not settled.
            </Typography.Text>
          )}
          <Space.Compact style={{ width: "100%" }}>
            <Input
              placeholder={mode === "order" ? "sales order id" : "row id"}
              value={query}
              onChange={(e) => setQuery(e.target.value.replace(/\D/g, ""))}
              onPressEnter={search}
            />
            <Button type="primary" onClick={search} loading={busy} disabled={!query}>
              Look up
            </Button>
          </Space.Compact>
        </Space>
      </Card>

      {rows !== null &&
        (rows.length === 0 ? (
          <Empty
            description={
              mode === "order"
                ? "Nothing unsettled on that order — every document and carrier call has landed."
                : "No such row"
            }
          />
        ) : (
          <Table
            rowKey={(r) => `${r.kind}-${r.id}`}
            size="small"
            pagination={false}
            dataSource={rows}
            expandable={{
              expandedRowRender: (r) => <RowDetail row={r} onResolved={replace} />,
              defaultExpandedRowKeys:
                rows.length === 1 ? [`${rows[0].kind}-${rows[0].id}`] : undefined,
            }}
            columns={[
              {
                title: "Row",
                render: (_: unknown, r: OutboxRow) =>
                  `${r.kind === "logistics" ? "op" : "doc"} #${r.id}`,
                width: 110,
              },
              { title: "What", render: (_: unknown, r: OutboxRow) => rowLabel(r) },
              {
                title: "State",
                width: 130,
                render: (_: unknown, r: OutboxRow) => (
                  <Tag color={SETTLED.includes(String(r.state)) ? "success" : "error"}>
                    {fmtText(r.state)}
                  </Tag>
                ),
              },
              {
                title: "Error",
                render: (_: unknown, r: OutboxRow) => (
                  <span style={{ color: "#cf1322", fontSize: 12 }}>{fmtText(r.error) || "—"}</span>
                ),
              },
            ]}
          />
        ))}
    </div>
  );
};

export default OmsOverrides;
